const RELOAD_FLAG_KEY = "spocket:chunk-load-retry";
const RELOAD_WINDOW_MS = 10_000;

/**
 * True if `error` is webpack's dynamic-import chunk failure — the classic "user has an old
 * tab open, a new version deployed, the old content-hashed chunk filename this page needs is
 * gone" problem. Covers three shapes we've seen in production:
 *  - `ChunkLoadError` (JS chunk 404/network failure)
 *  - plain `Error` with "Loading CSS chunk N failed" (webpack doesn't give CSS chunks their own
 *    error name)
 *  - `TypeError: Cannot use 'in' operator to search for 'default' in undefined` — Babel's module
 *    interop helper choking when a dynamic import() resolves to `undefined` instead of a module
 *    namespace object, which happens when the "chunk" request succeeds with a 200 but wrong
 *    content (e.g. a CDN/SPA fallback serving index.html for a since-removed asset path) rather
 *    than a clean network-level failure.
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  const message = error.message || String(error);
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w.-]+ failed/i.test(message) ||
    /Cannot use 'in' operator to search for 'default' in/i.test(message)
  );
}

/**
 * Reload the page once to pick up the new deploy's chunk manifest. Guards against a reload
 * loop (e.g. a genuinely broken/unreachable asset) with a timestamped sessionStorage flag: a
 * chunk error triggers a reload and stamps the current time; if *another* chunk error happens
 * within RELOAD_WINDOW_MS of that (meaning the reload didn't fix it), we give up and let the
 * caller show a manual fallback instead. A flag older than the window is treated as stale —
 * unrelated to whatever just failed — so a fresh chunk error much later in the same tab session
 * still gets its own auto-reload rather than being permanently blocked by a long-resolved one.
 * @return {boolean} true if a reload was triggered (caller should render nothing further),
 *                    false if we've already retried once inside the window and should show a
 *                    fallback UI.
 */
export function reloadOnceForChunkError() {
  const now = Date.now();
  const lastAttempt = Number(sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);

  if (lastAttempt && now - lastAttempt < RELOAD_WINDOW_MS) {
    return false;
  }

  sessionStorage.setItem(RELOAD_FLAG_KEY, String(now));
  window.location.reload();
  return true;
}
